export const insightBundle = {
  title: {
    ko: "AI {resource} 분석 인사이트",
    en: "AI {resource} Analysis Insight",
    es: "Insight de análisis de IA para {resource}"
  },

  subtitle: {
    ko: "데이터 기반의 세대 맞춤형 소비 진단 보고서",
    en: "Data-driven personalized household consumption diagnosis report",
    es: "Informe personalizado de diagnóstico de consumo basado en datos"
  },

  fallback: {
    title: {
      ko: "소비 종합 리포트 생성 중",
      en: "Generating Usage Summary Report",
      es: "Generando informe resumido de uso"
    },

    desc: {
      ko: "세대 정보와 사용량 검침 정보가 모두 로드되면 실시간 세대 맞춤형 가이드라인을 분석하여 출력합니다.",
      en: "When unit and metering data are fully loaded, real-time personalized guidance will be analyzed and displayed.",
      es: "Cuando se carguen completamente los datos de la unidad y del medidor, se mostrará una guía personalizada en tiempo real."
    }
  },

  leak: {
    detectedTitle: {
      ko: "미세 누수 및 손실 감지",
      en: "Micro Leak and Loss Detected",
      es: "Se detectó microfuga y pérdida"
    },

    safeTitle: {
      ko: "지속 손실(누수) 없음",
      en: "No Ongoing Loss or Leak Detected",
      es: "No se detectó pérdida o fuga continua"
    },

    detectedDesc: {
      ko: "최근 검침 데이터에서 지속적인 **미세 누수({rate}%)**가 포착되었습니다. 싱크대, 화장실 밸브, 내부 배관 상태를 점검해보세요.",
      en: "Recent metering data indicates a persistent **micro leak ({rate}%)**. Check sinks, toilet valves, and internal piping conditions.",
      es: "Los datos recientes del medidor indican una **microfuga persistente ({rate}%)**. Revise fregaderos, válvulas del inodoro y tuberías internas."
    },

    safeDesc: {
      ko: "24시간 지속되는 리소스 사용이나 누수 의심 패턴이 발견되지 않아 안정적으로 사용 중인 것으로 보입니다.",
      en: "No continuous leak-like pattern was detected over 24 hours. Usage appears stable.",
      es: "No se detectó un patrón continuo similar a una fuga durante 24 horas. El uso parece estable."
    }
  },

  compare: {
    title: {
      ko: "우리 단지 평균 대비 소비량",
      en: "Comparison with Complex Average",
      es: "Comparación con el promedio del complejo"
    },

    highDesc: {
      ko: "우리 집은 단지 평균보다 **{percent}% 더 많이** 사용하고 있습니다. 불필요하게 대기 중인 전력이나 낭비되는 요소를 점검할 필요가 있습니다.",
      en: "This household uses **{percent}% more** than the complex average. It is worth checking for unnecessary standby power or hidden waste factors.",
      es: "Este hogar consume **{percent}% más** que el promedio del complejo. Conviene revisar energía en espera innecesaria o factores de desperdicio."
    },

    lowDesc: {
      ko: "단지 평균 대비 **{percent}% 적게** 사용하며 모범적인 절약 패턴을 보이고 있습니다.",
      en: "This household uses **{percent}% less** than the complex average. It shows an exemplary conservation pattern.",
      es: "Este hogar consume **{percent}% menos** que el promedio del complejo. Muestra un patrón ejemplar de ahorro."
    },

    normalDesc: {
      ko: "단지 평균 대비 차이(상위 **{percent}%**)가 유사하게 현재 범위 내에서 안정적으로 소비하고 계십니다.",
      en: "Usage is within a stable range, with a similar gap to the complex average (top **{percent}%**).",
      es: "El uso se mantiene estable, con una diferencia similar al promedio del complejo (top **{percent}%**)."
    }
  },

  peak: {
    title: {
      ko: "시간대별 {resource} Peak 분석",
      en: "{resource} Peak Analysis by Time of Day",
      es: "Análisis de picos de {resource} por franja horaria"
    },

    desc: {
      ko: "최근 기준으로 보면 주로 **{peakText}**에 가장 많은 리소스가 집중됩니다. 해당 시간대 사용이나 소비 요소를 분산하면 피크치와 요금 부담을 줄이는 데 도움이 됩니다.",
      en: "Recent data shows the highest concentration of usage around **{peakText}**. Distributing usage during this time can help reduce peak costs and improve efficiency.",
      es: "Los datos recientes muestran la mayor concentración de uso alrededor de **{peakText}**. Distribuir el uso en este horario puede ayudar a reducir costos y mejorar la eficiencia."
    }
  },

  weekdayPattern: {
    title: {
      ko: "주간 라이프스타일 매칭",
      en: "Weekly Lifestyle Pattern Matching",
      es: "Coincidencia del estilo de vida semanal"
    },

    baseDesc: {
      ko: "일주일 중 **{peakDay}요일**에 가장 활발한 리소스 소비 패턴을 보입니다.",
      en: "The household shows the highest resource usage on **{peakDay}**.",
      es: "El hogar muestra el mayor uso de recursos el **{peakDay}**."
    },

    weekendDesc: {
      ko: "주중 대비 주말 사용량이 **{diffPercent}% 증가**하는 **주말 가족 집중형** 패턴입니다.",
      en: "Weekend usage **increases by {diffPercent}%** compared to weekdays, indicating a weekend-focused household pattern.",
      es: "El uso de fin de semana **aumenta un {diffPercent}%** frente a los días laborables, lo que indica un patrón centrado en el fin de semana."
    },

    weekdayDesc: {
      ko: "주말 대비 평일 사용량이 **{diffPercent}% 증가**하는 **평일 주거 집중형** 패턴입니다.",
      en: "Weekday usage **increases by {diffPercent}%** compared to weekends, indicating a weekday residence-focused pattern.",
      es: "El uso entre semana **aumenta un {diffPercent}%** frente al fin de semana, lo que indica un patrón centrado en los días laborables."
    },

    days: [
      { ko: "일", en: "Sun", es: "Dom" },
      { ko: "월", en: "Mon", es: "Lun" },
      { ko: "화", en: "Tue", es: "Mar" },
      { ko: "수", en: "Wed", es: "Mié" },
      { ko: "목", en: "Thu", es: "Jue" },
      { ko: "금", en: "Fri", es: "Vie" },
      { ko: "토", en: "Sat", es: "Sáb" }
    ]
  }
}
